import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import './HotelCategories.css';
import { AuthContext } from '../context/AuthContext';

const HotelCategories = () => {
  const { hasPermission } = useContext(AuthContext);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingStatus, setTogglingStatus] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/hotel-categories`);
      setCategories(response.data.data || []);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch hotel categories');
      setLoading(false);
    }
  };


  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hotel category?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/admin/hotel-categories/${id}`);
      fetchCategories();
    } catch (error) {
      setError('Failed to delete hotel category');
    }
  };

  const handleToggleActive = async (categoryId, currentStatus) => {
    const newStatus = !currentStatus;
    setTogglingStatus(prev => ({ ...prev, [categoryId]: true }));
    
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/hotel-categories/${categoryId}`, {
        isActive: newStatus
      });
      
      // Update the category in the local state
      setCategories(categories.map(category => 
        category._id === categoryId 
          ? { ...category, isActive: response.data.data.isActive }
          : category
      ));
    } catch (error) {
      setError('Failed to update category status');
    } finally {
      setTogglingStatus(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  const pagination = usePagination(categories, { noun: 'categories' });

  if (loading) {
    return (
      <Layout>
        <div className="container">
          <div className="categories-header">
            <h1>Hotel Categories</h1>
            {hasPermission('hotel-categories.create') && (
              <Link to="/sy-admin/hotel-categories/new" className="btn btn-primary">
                <span className="btn-icon">+</span>
                Add New Category
              </Link>
            )}
          </div>
          <div className="categories-table-container">
            <table className="categories-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                  <tr key={index} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '60%', height: '16px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '40px', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-switch" style={{ width: '100px', height: '26px' }}></div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container">
        <div className="categories-header">
          <h1>Hotel Categories</h1>
          {hasPermission('hotel-categories.create') && (
            <Link to="/sy-admin/hotel-categories/new" className="btn btn-primary">
              <span className="btn-icon">+</span>
              Add New Category
            </Link>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="categories-stats">
          <div className="stat-card">
            <h3>{categories.length}</h3>
            <p>Total Categories</p>
          </div>
          <div className="stat-card">
            <h3>{categories.filter(c => c.isActive).length}</h3>
            <p>Active Categories</p>
          </div>
        </div>

        {categories.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏨</div>
            <h2>No hotel categories found</h2>
            <p>Get started by adding your first hotel category to the platform.</p>
            {hasPermission('hotel-categories.create') && (
              <Link to="/sy-admin/hotel-categories/new" className="btn btn-primary">
                Add Your First Category
              </Link>
            )}
          </div>
        ) : (
          <div className="categories-table-container">
            <table className="categories-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Order</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagination.items.map((category) => {
                  const isTogglingStatus = togglingStatus[category._id];
                  return (
                    <tr key={category._id}>
                      <td>
                        <div className="category-name-cell">
                          <span className="category-name-text">{category.name}</span>
                        </div>
                      </td>
                      <td>
                        <div className="category-order-cell">
                          <span>{category.displayOrder}</span>
                        </div>
                      </td>
                      <td>
                        <div className="status-cell">
                          <label className="status-toggle-label">
                            <div className="toggle-switch">
                              <input
                                type="checkbox"
                                checked={category.isActive}
                                onChange={() => handleToggleActive(category._id, category.isActive)}
                                disabled={isTogglingStatus}
                                className="toggle-input"
                              />
                              <span className={`toggle-slider ${isTogglingStatus ? 'loading' : ''}`}></span>
                            </div>
                            <span className={`status-text ${category.isActive ? 'status-active-text' : 'status-inactive-text'}`}>
                              {category.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                        </div>
                      </td>
                      <td>
                        <div className="table-actions">
                          {hasPermission('hotel-categories.update') && (
                            <button
                              onClick={() => navigate(`/sy-admin/hotel-categories/edit/${category._id}`)}
                              className="btn btn-edit btn-icon-only"
                              title="Edit Category"
                            >
                              <span className="btn-icon">✏️</span>
                            </button>
                          )}
                          {hasPermission('hotel-categories.delete') && (
                            <button
                              onClick={() => handleDelete(category._id)}
                              className="btn btn-delete btn-icon-only"
                              title="Delete Category"
                            >
                              <span className="btn-icon">🗑️</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <Pagination {...pagination.props} />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HotelCategories;

